/**
 * Sign-in and sign-up.
 *
 * One component for both, because the only real difference is which Supabase
 * call runs and which copy is shown. Password rules are stated up front rather
 * than revealed as an error after submitting.
 */

import { useCallback, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, View, type TextInput } from 'react-native';
import { credentialsSchema, fieldErrors } from '@yummy/shared';
import { signInWithGoogle, signInWithPassword, signUpWithPassword } from '@yummy/db';
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  ErrorBanner,
  Screen,
  Separator,
  Stack,
  Text,
  TextField,
  useAction,
  useResponsive,
  useT,
  useTheme,
} from '@yummy/ui-rnr';

import { GoogleMark } from '../components/GoogleMark';
import { supabase } from '../lib/supabase';

/**
 * Where the browser should come back to after a provider or email round trip.
 *
 * The page the merchant is on, not the app's root. A pickup link is the case
 * that made this matter: they followed a customer's code, the gate asked them to
 * sign in, and returning them to the dashboard afterwards threw away the order
 * they were holding. Supabase matches its allow list by prefix, so a route under
 * an allowed origin is accepted.
 *
 * @returns The current URL.
 */
function returnTo(): string {
  return window.location.href;
}

/**
 * Renders the merchant sign-in or sign-up form.
 *
 * @param props Which of the two forms to render.
 * @returns The authentication screen.
 */
export function AuthScreen({ mode }: { mode: 'login' | 'signup' }) {
  const theme = useTheme();
  const t = useT();
  const { isTablet } = useResponsive();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const action = useAction();
  /** Enter in the email field moves here, rather than submitting a half-filled form. */
  const passwordRef = useRef<TextInput>(null);

  const isSignup = mode === 'signup';
  const submitLabel = isSignup ? t('merchant.auth.signUp') : t('merchant.auth.signIn');
  const switchLabel = isSignup ? t('merchant.auth.haveAccount') : t('merchant.auth.needAccount');

  const submit = useCallback(async () => {
    const parsed = credentialsSchema.safeParse({ email, password });
    if (!parsed.success) {
      setErrors(fieldErrors(parsed.error));
      return;
    }
    setErrors({});
    setNotice(null);

    if (isSignup) {
      const result = await action.run(() =>
        signUpWithPassword(supabase, parsed.data.email, parsed.data.password, returnTo()),
      );
      if (!result) return;
      if (result.needsEmailConfirmation) {
        setNotice(t('merchant.auth.confirmationSent', { email: parsed.data.email }));
      }
      // With confirmation disabled Supabase returns a session and the provider
      // picks it up, so no navigation is needed here.
      return;
    }

    await action.run(() => signInWithPassword(supabase, parsed.data.email, parsed.data.password));
  }, [email, password, isSignup, action, t]);

  const continueWithGoogle = useCallback(async () => {
    // The browser navigates away to Google, so this only returns when the
    // redirect could not be started — typically because the provider is not
    // enabled on the Supabase project yet.
    await action.run(() =>
      signInWithGoogle(supabase, returnTo()),
    );
  }, [action]);

  return (
    <Screen columnClassName="max-w-md">
      <Stack gap="xl" className={cn(isTablet ? 'pt-12' : 'pt-6')}>
        <Text className="text-primary text-3xl font-bold">{t('common.appName')}</Text>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {isSignup ? t('merchant.auth.signUpTitle') : t('merchant.auth.signInTitle')}
            </CardTitle>
            <CardDescription>
              {isSignup ? t('merchant.auth.signUpIntro') : t('merchant.auth.signInIntro')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Stack gap="lg">
              <TextField
                label={t('common.email')}
                required
                value={email}
                onChangeText={setEmail}
                error={errors.email}
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect={false}
                placeholder={t('merchant.auth.emailPlaceholder')}
                returnKeyType="next"
                submitBehavior="submit"
                onSubmitEditing={() => passwordRef.current?.focus()}
              />
              <TextField
                ref={passwordRef}
                label={t('common.password')}
                required
                value={password}
                onChangeText={setPassword}
                error={errors.password}
                secureTextEntry
                autoComplete={isSignup ? 'new-password' : 'current-password'}
                placeholder={t('merchant.auth.passwordPlaceholder')}
                onSubmitEditing={submit}
                hint={isSignup ? t('merchant.auth.passwordHint') : undefined}
              />

              {action.error ? (
                <ErrorBanner
                  message={
                    // Supabase answers a disabled provider with a JSON body no
                    // merchant can read; that case has one meaning and one
                    // sentence in the dictionary. Every other failure — a dropped
                    // connection, a rejected redirect — keeps the server's words.
                    /provider is not enabled/i.test(action.error)
                      ? t('merchant.auth.googleUnavailable')
                      : action.error
                  }
                />
              ) : null}
              {notice ? (
                <Callout tone="success" title={t('merchant.auth.checkInbox')} message={notice} />
              ) : null}

              <Button
                onPress={submit}
                disabled={action.pending}
                size="lg"
                accessibilityLabel={submitLabel}
                aria-busy={action.pending}
                className="w-full">
                {action.pending ? (
                  <ActivityIndicator color={theme.palette.primaryForeground} size="small" />
                ) : (
                  <Text>{submitLabel}</Text>
                )}
              </Button>

              {/* The Reusables social-connections block's shape: the provider
                  action below a divider, so it reads as an alternative to the
                  password form rather than a second submit. */}
              <View className="flex-row items-center">
                <Separator className="flex-1" />
                <Text className="text-muted-foreground px-4 text-sm">
                  {t('merchant.auth.orDivider')}
                </Text>
                <Separator className="flex-1" />
              </View>

              <Button
                variant="outline"
                size="lg"
                className="w-full"
                disabled={action.pending}
                accessibilityLabel={t('merchant.auth.continueWithGoogle')}
                onPress={() => void continueWithGoogle()}>
                <GoogleMark size={18} />
                <Text>{t('merchant.auth.continueWithGoogle')}</Text>
              </Button>

              {/* The block's footer link, kept as a link rather than a second
                  button: one primary action per form. */}
              <Pressable
                accessibilityRole="link"
                accessibilityLabel={switchLabel}
                onPress={() => {
                  window.location.href = isSignup ? '/login' : '/signup';
                }}>
                <Text className="text-center text-sm underline underline-offset-4">
                  {switchLabel}
                </Text>
              </Pressable>
            </Stack>
          </CardContent>
        </Card>

        <Text className="text-subtle-foreground text-center text-xs">
          {t('merchant.auth.terms')}
        </Text>
      </Stack>
    </Screen>
  );
}
